import clsx from 'clsx';
import { ArrowRight, FilePlus2, FileX2, MoveVertical, PencilLine, Tag } from 'lucide-react';
import { useState } from 'react';
import type { DocumentDiff, LineHunk, SectionDiff } from '@imsenta/shared';
import { Badge, Tabs, type BadgeTone } from './ui';

const KIND_META: Record<SectionDiff['kind'], { label: string; tone: BadgeTone; icon: typeof PencilLine }> = {
  added: { label: 'Added', tone: 'success', icon: FilePlus2 },
  removed: { label: 'Removed', tone: 'danger', icon: FileX2 },
  modified: { label: 'Edited', tone: 'warning', icon: PencilLine },
  renamed: { label: 'Renamed', tone: 'info', icon: Tag },
  moved: { label: 'Moved', tone: 'purple', icon: MoveVertical },
  unchanged: { label: 'Unchanged', tone: 'neutral', icon: PencilLine },
};

export function DiffSummary({ diff }: { diff: DocumentDiff }) {
  const { summary } = diff;
  const chips: [string, number, BadgeTone][] = [
    ['added', summary.sectionsAdded, 'success'],
    ['removed', summary.sectionsRemoved, 'danger'],
    ['edited', summary.sectionsModified, 'warning'],
    ['renamed', summary.sectionsRenamed, 'info'],
    ['moved', summary.sectionsMoved, 'purple'],
  ];
  const changed = chips.reduce((sum, [, n]) => sum + n, 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {changed === 0 ? (
        <Badge tone="neutral">No changes</Badge>
      ) : (
        chips
          .filter(([, n]) => n > 0)
          .map(([label, n, tone]) => (
            <Badge key={label} tone={tone}>
              {n} {label}
            </Badge>
          ))
      )}
      {summary.wordsAdded || summary.wordsRemoved ? (
        <span className="text-[12.5px] text-ink-500">
          <span className="font-medium text-emerald-700">+{summary.wordsAdded}</span>{' '}
          <span className="font-medium text-rose-700">−{summary.wordsRemoved}</span> words
        </span>
      ) : null}
      {/* Only meaningful when wording was removed or replaced: a pure addition
          always keeps 100% of the previous text, which tells the reader nothing. */}
      {changed > 0 && summary.wordsRemoved > 0 ? (
        <span className="text-[12.5px] text-ink-400" title="How much of the earlier wording survives in this version">
          {Math.round(summary.similarity * 100)}% of the previous text kept
        </span>
      ) : null}
    </div>
  );
}

function InlineDiff({ section }: { section: SectionDiff }) {
  if (!section.changes.length) return <p className="text-[13px] text-ink-400 italic">This section has no body text.</p>;
  return (
    <p className="pf-diff text-[13.5px] leading-relaxed whitespace-pre-wrap text-ink-700">
      {section.changes.map((change, index) =>
        change.type === 'add' ? (
          <ins key={index}>{change.value}</ins>
        ) : change.type === 'remove' ? (
          <del key={index}>{change.value}</del>
        ) : (
          <span key={index}>{change.value}</span>
        ),
      )}
    </p>
  );
}

function SideBySide({ section }: { section: SectionDiff }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div>
        <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-ink-400 uppercase">Before</p>
        <div className="rounded-lg border border-rose-100 bg-rose-50/50 p-3 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
          {section.previousText || <span className="text-ink-400 italic">Empty</span>}
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-ink-400 uppercase">After</p>
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-700">
          {section.currentText || <span className="text-ink-400 italic">Empty</span>}
        </div>
      </div>
    </div>
  );
}

export function SectionDiffList({ diff, defaultShowUnchanged = false }: { diff: DocumentDiff; defaultShowUnchanged?: boolean }) {
  const [view, setView] = useState<'inline' | 'split'>('inline');
  const [showUnchanged, setShowUnchanged] = useState(defaultShowUnchanged);

  const sections = diff.sections.filter((s) => showUnchanged || s.kind !== 'unchanged');
  const unchangedCount = diff.sections.filter((s) => s.kind === 'unchanged').length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Tabs
          tabs={[
            { id: 'inline' as const, label: 'Inline' },
            { id: 'split' as const, label: 'Side by side' },
          ]}
          value={view}
          onChange={setView}
          className="border-0"
        />
        {unchangedCount > 0 ? (
          <label className="flex items-center gap-2 text-[12.5px] text-ink-600">
            <input
              type="checkbox"
              checked={showUnchanged}
              onChange={(e) => setShowUnchanged(e.target.checked)}
              className="size-3.5 rounded border-ink-300 text-brand-600 focus:ring-brand-400"
            />
            Show {unchangedCount} unchanged section{unchangedCount === 1 ? '' : 's'}
          </label>
        ) : null}
      </div>

      {sections.length === 0 ? (
        <p className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-8 text-center text-[13.5px] text-ink-500">
          These two versions are identical.
        </p>
      ) : (
        <ul className="space-y-3">
          {sections.map((section) => {
            const meta = KIND_META[section.kind];
            const Icon = meta.icon;
            return (
              <li key={`${section.sectionId}-${section.kind}`} className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 bg-ink-50/60 px-4 py-2.5">
                  <Icon className="size-3.5 text-ink-400" />
                  <span className="font-mono text-[11.5px] text-ink-400">
                    {section.previousNumber && section.previousNumber !== section.number ? (
                      <>
                        {section.previousNumber} <ArrowRight className="inline size-3" /> {section.number ?? '—'}
                      </>
                    ) : (
                      (section.number ?? section.previousNumber ?? '·')
                    )}
                  </span>
                  <span className="text-[13.5px] font-semibold text-ink-800">
                    {section.previousTitle ? (
                      <>
                        <span className="text-ink-400 line-through">{section.previousTitle}</span> <ArrowRight className="inline size-3 text-ink-400" />{' '}
                        {section.title}
                      </>
                    ) : (
                      section.title
                    )}
                  </span>
                  <Badge tone={meta.tone} className="ml-auto">
                    {meta.label}
                  </Badge>
                  {section.addedWords || section.removedWords ? (
                    <span className="text-[11.5px]">
                      <span className="text-emerald-700">+{section.addedWords}</span> <span className="text-rose-700">−{section.removedWords}</span>
                    </span>
                  ) : null}
                </div>
                <div className={clsx('px-4 py-3', section.kind === 'unchanged' && 'opacity-60')}>
                  {view === 'inline' ? <InlineDiff section={section} /> : <SideBySide section={section} />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Raw Markdown line diff, for anyone who wants to see the exact stored text. */
export function MarkdownDiff({ hunks }: { hunks: LineHunk[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-ink-200 bg-white">
      <table className="w-full border-collapse font-mono text-[12px]">
        <tbody>
          {hunks.flatMap((hunk, hunkIndex) =>
            hunk.lines.map((line, lineIndex) => {
              const oldNo = hunk.type === 'add' ? null : hunk.oldStart + lineIndex;
              const newNo = hunk.type === 'remove' ? null : hunk.newStart + lineIndex;
              return (
                <tr
                  key={`${hunkIndex}-${lineIndex}`}
                  className={clsx(
                    hunk.type === 'add' && 'bg-emerald-50',
                    hunk.type === 'remove' && 'bg-rose-50',
                  )}
                >
                  <td className="w-10 border-r border-ink-100 px-2 py-0.5 text-right align-top text-ink-300 select-none">{oldNo ?? ''}</td>
                  <td className="w-10 border-r border-ink-100 px-2 py-0.5 text-right align-top text-ink-300 select-none">{newNo ?? ''}</td>
                  <td className="w-5 px-1 py-0.5 text-center align-top select-none">
                    <span className={clsx(hunk.type === 'add' && 'text-emerald-600', hunk.type === 'remove' && 'text-rose-600')}>
                      {hunk.type === 'add' ? '+' : hunk.type === 'remove' ? '−' : ' '}
                    </span>
                  </td>
                  <td className="px-2 py-0.5 whitespace-pre-wrap text-ink-700">{line || ' '}</td>
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );
}
